import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientsApi } from '../utils/api';

export default function Dashboard() {
  const [clients, setClients] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientsApi.getAll().then(async r => {
      const c = r.data.filter(cl => !cl.archived);
      setClients(c);
      const summaryMap = {};
      await Promise.all(c.map(async client => {
        try {
          const s = await clientsApi.dashboardSummary(client.id);
          summaryMap[client.id] = s.data;
        } catch (e) {
          summaryMap[client.id] = { coveredThisWeek: false, savedCount: 0 };
        }
      }));
      setSummaries(summaryMap);
    }).finally(() => setLoading(false));
  }, []);

  const covered = clients.filter(c => summaries[c.id]?.coveredThisWeek).length;
  const needsPitch = clients.filter(c => !summaries[c.id]?.coveredThisWeek).length;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const greetings = ['Good morning', 'Hey there', 'Welcome back'];
  const greeting = greetings[new Date().getDay() % greetings.length];

  return (
    <div className="page-content">
      <div className="page-header flex-between">
        <div>
          <h1>{greeting} 👋</h1>
          <p>{today}</p>
        </div>
        <Link to="/clients" className="btn btn-primary">+ New Client</Link>
      </div>

      {/* 3 big stat cards */}
      <div className="grid-3" style={{ marginBottom: 36 }}>
        <div className="card" style={{ textAlign: 'center', padding: '28px 20px' }}>
          <div style={{ fontSize: 48, fontWeight: 800, fontFamily: 'Syne, sans-serif', lineHeight: 1, color: 'var(--text-primary)' }}>
            {loading ? '—' : clients.length}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8, fontWeight: 500 }}>Total Clients</div>
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '28px 20px', borderColor: covered > 0 ? 'rgba(34,197,94,0.3)' : 'var(--border)' }}>
          <div style={{ fontSize: 48, fontWeight: 800, fontFamily: 'Syne, sans-serif', lineHeight: 1, color: 'var(--green)' }}>
            {loading ? '—' : covered}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8, fontWeight: 500 }}>Covered This Week</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>have a podcast ready</div>
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '28px 20px', borderColor: needsPitch > 0 ? 'rgba(245,158,11,0.3)' : 'var(--border)' }}>
          <div style={{ fontSize: 48, fontWeight: 800, fontFamily: 'Syne, sans-serif', lineHeight: 1, color: needsPitch > 0 ? 'var(--amber)' : 'var(--text-muted)' }}>
            {loading ? '—' : needsPitch}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8, fontWeight: 500 }}>Need Pitching</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>no podcast yet this week</div>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><p style={{ color: 'var(--text-muted)' }}>Loading...</p></div>
      ) : clients.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎙</div>
          <h3>No clients yet</h3>
          <p>Add your first client to start tracking</p>
          <Link to="/clients" className="btn btn-primary mt-3">Add First Client</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Needs pitching first */}
          {clients.filter(c => !summaries[c.id]?.coveredThisWeek).map(client => (
            <ClientRow key={client.id} client={client} summary={summaries[client.id]} />
          ))}
          {/* Covered */}
          {clients.filter(c => summaries[c.id]?.coveredThisWeek).map(client => (
            <ClientRow key={client.id} client={client} summary={summaries[client.id]} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientRow({ client, summary = {} }) {
  const covered = summary.coveredThisWeek;
  return (
    <div className="card" style={{
      display: 'flex', alignItems: 'center', gap: 16,
      borderLeft: `3px solid ${covered ? 'var(--green)' : 'var(--amber)'}`
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: covered ? 'var(--green-soft)' : 'var(--amber-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16,
        color: covered ? 'var(--green)' : 'var(--amber)'
      }}>
        {client.name.charAt(0).toUpperCase()}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{client.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {covered
            ? `${summary.savedCount} podcast${summary.savedCount !== 1 ? 's' : ''} saved`
            : 'No podcast saved yet'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
        {summary.pitched > 0 && <span>📤 {summary.pitched} pitched</span>}
        {summary.followedUp > 0 && <span>🔄 {summary.followedUp} followed up</span>}
        {summary.booked > 0 && <span>🎙 {summary.booked} booked</span>}
      </div>

      <span className={`badge ${covered ? 'badge-green' : 'badge-amber'}`}>
        {covered ? '✓ Covered' : '⚠ Needs Pitch'}
      </span>

      <Link to={`/clients/${client.id}`} className="btn btn-secondary btn-sm">View</Link>
    </div>
  );
}
